/**
 * Canonical default title for a new libra ticket: `<Workflow Name> - <Scope Name>`,
 * or just the workflow name when the ticket has no narrower scope.
 *
 * The workflow is the only required part. Tickets are not necessarily
 * unit-scoped — they may be asset-independent, or finer-grained than a unit
 * (per component, per inspection) — so the scope name is optional and callers
 * pass whatever their surface is scoped to. A workflow-configured title template
 * would be the fuller answer if more scopes appear.
 *
 * Returns `undefined` when the workflow is unknown rather than filling a partial
 * title: callers auto-fill a form field from this, and the workflow list resolves
 * asynchronously, so they must be able to distinguish "not resolvable yet" from
 * "resolved to empty". Blanking the field strands it, because nothing re-runs.
 *
 * Named arguments are deliberate — the parts are all strings and the output order
 * is the reverse of any "scope, workflow" reading, so positional parameters would
 * make a silently-reversed title easy to write.
 *
 * The two parameters are optional in different ways on purpose: `scopeName` may be
 * omitted entirely, because a scope-less ticket is a valid ticket. `workflowName`
 * must always be passed, because callers resolve it asynchronously and `undefined`
 * is a meaningful "not known yet" that this function reports back as `undefined`.
 *
 * `ticketMenu/CreateTicket.tsx` and `dashboard/CreateTicketsForGroupModal.tsx`
 * still inline this template and should be migrated here.
 */
export function defaultTicketTitle({
  scopeName,
  workflowName,
}: {
  scopeName?: string
  workflowName: string | undefined
}): string | undefined {
  if (!workflowName) return undefined
  return scopeName ? `${workflowName} - ${scopeName}` : workflowName
}
